/**
 * 文件夹配额视图模型派生（纯函数）· 档位 + used/cap → widget 视图状态
 *
 * 故意不 import src/constants/config —— 该文件引用 webpack 全局
 * __DESKTOP_DOWNLOAD_URL__，在 Jest 环境会 ReferenceError 崩掉本纯函数单测。
 * 这里本地镜像 VIP_KIND 的 4 个值（后端协议固定，不会变；若与 config 不一致即 bug）。
 */

import { resolveDisplayCap } from '../../components/FolderPaywall/folderTiers'
import type { TierInfo } from '../../components/FolderPaywall/folderTiers'

/** 镜像 CONFIG.VIP_KIND（src/constants/config.js）· 必须与之同步 */
const VK = { VIP: 1, SVIP: 2, VVIP: 3, EVIP: 4 } as const

/**
 * 语义档位顺序（低→高）· 与 VipPaymentModal 的 rankOrder 一致。
 * 注意 vip_kind 数值非单调（基础1<高级3<超级2<企业4），排序必须靠这个数组。
 */
const VIP_TIER_ORDER: number[] = [VK.VIP, VK.VVIP, VK.SVIP, VK.EVIP]

export interface QuotaView {
  /** 是否渲染 widget · 非会员=false */
  visible: boolean
  /** 是否企业级（无升级入口） */
  isEnterprise: boolean
  /** 用量状态 */
  state: 'normal' | 'approaching' | 'exceeded'
  /** cap 展示串 · String(展示 cap)；接口4 的 -1 已归一到超级档 cap */
  capDisplay: string
  /** 是否显示「升级扩容」· 企业级=false */
  showUpgrade: boolean
  /** 升级目标 vip_kind · 仅 showUpgrade 时有意义 */
  upgradeTargetVipKind: number
  /** 升级扩容付费来源 source_detail（PRD §3.2.1）· 无升级时为空串 */
  sourceDetail: string
}

/**
 * 已超限时按 used 区间定位升级档（PRD §3.2）· 只看 used，与当前档无关。
 * 理论上高档用户 used 较小时可能指向更低档，但高档 cap 很大，used>cap 必然 used>1800，
 * 故实际不会出现"降档"。
 */
function exceededTarget(used: number): number {
  if (used <= 270) return VK.VVIP
  if (used <= 1800) return VK.SVIP
  return VK.EVIP
}

/** 当前档 +1（语义顺序）· 顶到企业级保持企业级；未知档兜底→高级 */
function nextTier(vipKind: number): number {
  const i = VIP_TIER_ORDER.indexOf(vipKind)
  if (i < 0) return VK.VVIP
  return VIP_TIER_ORDER[Math.min(i + 1, VIP_TIER_ORDER.length - 1)]
}

/**
 * 常驻入口「升级扩容」付费来源 source_detail（PRD §3.2.1）· 按【当前】会员档位。
 * 注意与批量/单个创建（按升级目标档定 detail）不同：常驻入口表只有基础/高级/超级三档，
 * 若按升级目标档则 Vip 永远命中不到（目标恒 ≥ 高级），故只能按当前档。
 * 企业级 / 非会员不展示升级 → 空串。
 */
function quotaSourceDetail(vipKind: number): string {
  switch (vipKind) {
    case VK.VIP:
      return 'groupFolderNumLimitVip'
    case VK.VVIP:
      return 'groupFolderNumLimitVvip'
    case VK.SVIP:
      return 'groupFolderNumLimitSvip'
    default:
      return ''
  }
}

export function deriveQuotaView(input: {
  vipKind: number
  used: number
  cap: number
  tiers?: TierInfo[]
}): QuotaView {
  const { vipKind, used, cap, tiers } = input

  if (vipKind === 0) {
    return {
      visible: false,
      isEnterprise: false,
      state: 'normal',
      capDisplay: '',
      showUpgrade: false,
      upgradeTargetVipKind: 0,
      sourceDetail: ''
    }
  }

  // 超级 / 企业级团队接口4 的 cap 返回 -1（"无上限"哨兵）：
  //  · 展示落到超级档具体配额（resolveDisplayCap：tiers 的 GroupSVip，其也为 -1 时回落 2000）
  //  · 但实际不限制 → 用量状态恒 normal，不因 used 高于展示值就判超限/接近
  const isUnlimited = cap === -1
  const displayCap = resolveDisplayCap(cap, tiers)

  if (vipKind === VK.EVIP) {
    return {
      visible: true,
      isEnterprise: true,
      state: 'normal',
      capDisplay: String(displayCap),
      showUpgrade: false,
      upgradeTargetVipKind: 0,
      sourceDetail: ''
    }
  }

  const state: QuotaView['state'] = isUnlimited
    ? 'normal'
    : used > displayCap
      ? 'exceeded'
      : displayCap > 0 && used / displayCap >= 0.9
        ? 'approaching'
        : 'normal'

  return {
    visible: true,
    isEnterprise: false,
    state,
    capDisplay: String(displayCap),
    showUpgrade: true,
    upgradeTargetVipKind:
      state === 'exceeded' ? exceededTarget(used) : nextTier(vipKind),
    sourceDetail: quotaSourceDetail(vipKind)
  }
}
